'use client';

import { datadogLogs } from '@datadog/browser-logs';
import { Suspense, useEffect } from 'react';
import { usePageTrack } from '@tv2/lib-offspring/hooks/tracking/usePageTrack';
import { CatchAllError } from '@/components/errors/Error';
import { ErrorPageChrome } from '@/components/errors/ErrorPageChrome';
import { MainProviders } from '@/app/providers';

function ErrorContent({ error }: { error: Error & { digest?: string } }) {
  usePageTrack({
    pageType: 'error',
    httpCode: '500',
  });

  useEffect(() => {
    datadogLogs.logger.error(error.message, {
      error: {
        message: error.message,
        stack: error.stack,
        type: 'offspring',
      },
      context: 'offspring/error',
      location: window.location.href,
    });
  }, [error]);

  return <CatchAllError />;
}

/**
 * Runtime error boundary. This boundary can be triggered before/outside a
 * route's `(app)` layout, so it provides its own provider tree via
 * `MainProviders` (tracking + error UI depend on the user/profile/subscription
 * contexts).
 *
 * The provider tree is wrapped in `<Suspense>` because the navigation chrome
 * calls `useSearchParams()`, which Next.js requires to be inside a Suspense
 * boundary when a route can be statically prerendered.
 */
export default function Error({
  error,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  return (
    <Suspense>
      <MainProviders environment={null}>
        <ErrorPageChrome>
          <ErrorContent error={error} />
        </ErrorPageChrome>
      </MainProviders>
    </Suspense>
  );
}
